<template>
	<div class="address-autocomplete" @focusout="onFocusOut">
		<v-text-field
			v-bind="$attrs"
			v-on="fieldListeners"
			:value="value"
			:autocomplete="enabled ? 'off' : $attrs.autocomplete"
			:role="enabled ? 'combobox' : undefined"
			:aria-expanded="enabled ? String(open) : undefined"
			:aria-controls="enabled ? listId : undefined"
			:aria-activedescendant="open && active >= 0 ? `${listId}-${active}` : undefined"
			aria-autocomplete="list"
			@input="onInput"
			@keydown="onKeydown"
		></v-text-field>
		<v-card v-if="open" class="address-suggestions" elevation="4">
			<v-list :id="listId" role="listbox" dense class="py-1">
				<v-list-item
					v-for="(suggestion, index) in suggestions"
					:id="`${listId}-${index}`"
					:key="suggestion.id"
					role="option"
					:aria-selected="String(index === active)"
					:input-value="index === active"
					@mousedown.native.prevent
					@click="choose(suggestion)"
				>
					<v-list-item-content class="py-1">
						<v-list-item-title class="suggestion-main">{{ suggestion.mainText }}</v-list-item-title>
						<v-list-item-subtitle v-if="suggestion.secondaryText">{{ suggestion.secondaryText }}</v-list-item-subtitle>
					</v-list-item-content>
				</v-list-item>
			</v-list>
			<div class="attribution">Google Maps</div>
		</v-card>
	</div>
</template>

<script>
	// Address text field with Google Places suggestions (Vue 2 counterpart of moxie-app's
	// AddressAutocompleteField). v-model is the field's own text; choosing a suggestion emits
	// `select` with the parsed address (see parseAddressComponents) and the parent fills its fields.
	// The list renders inline below the field so it inherits form branding and works in iframes.
	import { fetchPlaceAddress, fetchSuggestions, newSessionToken, placesAvailable } from '@/modules/utils/googlePlaces';

	const MIN_CHARACTERS = 3;
	const DEBOUNCE_MS = 250;
	let nextListId = 0;

	export default {
		name: 'AddressAutocomplete',
		inheritAttrs: false,
		props: { value: { type: String, default: '' } },

		data() {
			nextListId += 1;
			return {
				enabled: placesAvailable(),
				listId: `address-suggestions-${nextListId}`,
				open: false,
				suggestions: [],
				active: -1,
				sessionToken: null,
				timer: null,
				request: 0,
			};
		},

		computed: {
			fieldListeners() {
				// eslint-disable-next-line no-unused-vars
				const { input, select, ...rest } = this.$listeners;
				return rest;
			},
		},

		beforeDestroy() {
			clearTimeout(this.timer);
		},

		methods: {
			close() {
				clearTimeout(this.timer);
				this.request += 1;
				this.open = false;
				this.suggestions = [];
				this.active = -1;
			},

			async search(input) {
				const current = ++this.request;
				try {
					if (!this.sessionToken) this.sessionToken = await newSessionToken();
					const results = await fetchSuggestions(input, this.sessionToken);
					if (current !== this.request) return;
					this.suggestions = results;
					this.active = -1;
					this.open = results.length > 0;
				} catch (err) {
					// Suggestions are optional; the field keeps working as plain text.
					if (current === this.request) this.close();
				}
			},

			onInput(value) {
				this.$emit('input', value);
				if (!this.enabled) return;
				const input = String(value || '').trim();
				clearTimeout(this.timer);
				if (input.length < MIN_CHARACTERS) {
					this.close();
					return;
				}
				this.timer = setTimeout(() => this.search(input), DEBOUNCE_MS);
			},

			async choose(suggestion) {
				this.close();
				this.$emit('input', suggestion.text);
				try {
					this.$emit('select', await fetchPlaceAddress(suggestion));
				} catch (err) {
					// Keep the suggestion text; the remaining fields can be completed by hand.
				} finally {
					this.sessionToken = null;
				}
			},

			onKeydown(event) {
				if (!this.open || !this.suggestions.length) return;
				const count = this.suggestions.length;
				if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
					event.preventDefault();
					this.active = (this.active + (event.key === 'ArrowDown' ? 1 : -1) + count) % count;
				} else if (event.key === 'Enter' && this.active >= 0) {
					event.preventDefault();
					this.choose(this.suggestions[this.active]);
				} else if (event.key === 'Escape') {
					event.stopPropagation();
					this.close();
				}
			},

			onFocusOut(event) {
				if (!this.$el.contains(event.relatedTarget)) this.close();
			},
		},
	};
</script>

<style scoped lang="scss">
	.address-autocomplete {
		position: relative;
		min-width: 0;
	}
	.address-suggestions {
		position: absolute;
		top: 100%;
		left: 0;
		right: 0;
		z-index: 20;
		margin-top: 4px;
		max-height: 320px;
		overflow-y: auto;
		text-align: left;
	}
	.suggestion-main {
		font-weight: 600;
	}
	.attribution {
		padding: 4px 12px 6px;
		font-size: 12px;
		text-align: right;
		opacity: 0.7;
	}
</style>
